import type {
  DeskPackageConfiguration,
  PackageBenefit,
  PackageDurationUnit,
} from '@mds/shared/types/rate-card';
import { cn } from '@mds/shared/lib/utils';
import Select from '@mds/shared/ui/select';

import Button from '@/components/ui/button';
import Field, { InlineError, inputCls, selectCls } from '@/components/ui/field';
import {
  ACCESS_EARLIEST_START,
  ACCESS_LATEST_END,
  ACCESS_STEP,
  WEEKDAYS,
  benefitIdFrom,
} from '@/domain/packages';
import { clockLabel } from '@mds/shared/lib/dates';
import { type FieldBinder, useInlineField } from '@/lib/validation';

/**
 * Half-hour steps between noon and midnight, the hours the coworking floor
 * sells packages for. Midnight reads as the end of the day.
 */
const SLOTS = (ACCESS_LATEST_END - ACCESS_EARLIEST_START) / ACCESS_STEP;
const STARTS = Array.from({ length: SLOTS }, (_, i) => ACCESS_EARLIEST_START + i * ACCESS_STEP);
const ENDS = STARTS.map((m) => m + ACCESS_STEP);
const timeLabel = (m: number) => (m === 1440 ? 'Midnight' : clockLabel(m));

/**
 * The times on offer, plus the package's own when it was saved outside them —
 * a window from before the 12 PM limit — so it still reads as what it is.
 */
const timeOptions = (times: number[], current: number) =>
  times.includes(current)
    ? times.map((m) => ({ value: String(m), label: timeLabel(m) }))
    : [
        { value: String(current), label: `${timeLabel(current)} · saved before the 12 PM limit` },
        ...times.map((m) => ({ value: String(m), label: timeLabel(m) })),
      ];

/**
 * One desk package's rules: how long it lasts, when it can be used, who may
 * buy it, and what it includes.
 *
 * The term is counted in the unit the package is priced by — the API rejects
 * anything else — so that unit is shown, not chosen. Benefits are descriptive
 * only, and only a title is asked for: nothing here creates a balance or a
 * discount.
 */
export default function PackageEditor({
  value,
  unit,
  onChange,
  readOnly,
  field,
}: {
  value: DeskPackageConfiguration;
  unit: PackageDurationUnit;
  onChange: (next: DeskPackageConfiguration) => void;
  readOnly: boolean;
  /** The page's validation, with paths relative to this package's rules. */
  field: FieldBinder;
}) {
  const inline = useInlineField(field);
  const term = field('duration.count');
  const days = inline('access.daysOfWeek');
  const set = (patch: Partial<DeskPackageConfiguration>) => onChange({ ...value, ...patch });
  const setBenefit = (index: number, patch: Partial<PackageBenefit>) =>
    set({ benefits: value.benefits.map((b, i) => (i === index ? { ...b, ...patch } : b)) });

  const addBenefit = () => {
    const taken = new Set(value.benefits.map((b) => b.id));
    // The id is fixed when the row is added and never follows the title, so
    // renaming a benefit later does not change which benefit it is.
    set({
      benefits: [...value.benefits, { id: benefitIdFrom('', taken), title: '', description: '' }],
    });
  };

  const toggleDay = (day: number) => {
    if (!value.access) return;
    const days = value.access.daysOfWeek.includes(day)
      ? value.access.daysOfWeek.filter((d) => d !== day)
      : [...value.access.daysOfWeek, day].sort((a, b) => a - b);
    set({ access: { ...value.access, daysOfWeek: days } });
  };

  return (
    <div className="flex flex-col gap-6">
      <label className="flex items-center gap-2.5 text-base">
        <input
          type="checkbox"
          className="h-6 w-6 accent-brand-edge"
          checked={value.isAvailable}
          disabled={readOnly}
          onChange={(e) => set({ isAvailable: e.target.checked })}
        />
        On sale
        <span className="text-ink-muted">
          (off keeps it on existing bookings but hides it from new quotes and the public site)
        </span>
      </label>

      <Field label="Term" required hint={`How long one purchase lasts, in ${unit}s.`} {...term}>
        {/* Field marks its child, and the child here is a wrapper, so the mark
            goes on the input instead — that is what Save moves focus to. */}
        <div className="flex items-center gap-3" aria-invalid={false}>
          <input
            type="number"
            min={1}
            max={60}
            className={inputCls + ' w-28'}
            value={value.duration.count}
            disabled={readOnly}
            aria-invalid={term.error ? true : undefined}
            onChange={(e) =>
              set({ duration: { unit, count: Math.trunc(Number(e.target.value)) || 0 } })
            }
          />
          <span className="text-lg text-ink-secondary">
            {unit}
            {value.duration.count === 1 ? '' : 's'}
          </span>
        </div>
      </Field>

      <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
        <legend className="mb-2 font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
          When it can be used
        </legend>
        <label className="flex items-center gap-2.5 text-base">
          <input
            type="checkbox"
            className="h-5 w-5 accent-brand-edge"
            checked={value.access !== null}
            disabled={readOnly}
            onChange={(e) =>
              set({
                access: e.target.checked
                  ? {
                      daysOfWeek: [1, 2, 3, 4, 5],
                      startMinute: ACCESS_EARLIEST_START,
                      endMinute: ACCESS_LATEST_END,
                    }
                  : null,
              })
            }
          />
          Only on certain days or hours
        </label>
        {value.access === null ? (
          <p className="m-0 text-base text-ink-muted">Any time the coworking floor is open.</p>
        ) : (
          <>
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label="Days"
              tabIndex={-1}
              {...days.props}
            >
              {WEEKDAYS.map((d) => {
                const on = value.access?.daysOfWeek.includes(d.value) ?? false;
                return (
                  <button
                    key={d.value}
                    type="button"
                    disabled={readOnly}
                    aria-pressed={on}
                    onClick={() => toggleDay(d.value)}
                    className={cn(
                      'min-h-(--target-min) rounded-xl border px-3 text-base font-semibold transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color) disabled:opacity-60',
                      on
                        ? 'border-cell-selected-bg bg-cell-selected-bg text-cell-selected-fg'
                        : 'border-hairline bg-surface text-ink-muted hover:border-brand-edge',
                    )}
                  >
                    {d.short}
                  </button>
                );
              })}
            </div>
            <InlineError {...days.message} />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="From"
                hint="12 PM at the earliest."
                required
                {...field('access.startMinute')}
              >
                <Select
                  variant="field"
                  className={selectCls}
                  value={String(value.access.startMinute)}
                  disabled={readOnly}
                  onChange={(v) =>
                    value.access && set({ access: { ...value.access, startMinute: Number(v) } })
                  }
                  options={timeOptions(STARTS, value.access.startMinute)}
                  aria-label="Access starts"
                />
              </Field>
              <Field label="Until" required {...field('access.endMinute')}>
                <Select
                  variant="field"
                  className={selectCls}
                  value={String(value.access.endMinute)}
                  disabled={readOnly}
                  onChange={(v) =>
                    value.access && set({ access: { ...value.access, endMinute: Number(v) } })
                  }
                  options={timeOptions(ENDS, value.access.endMinute)}
                  aria-label="Access ends"
                />
              </Field>
            </div>
          </>
        )}
      </fieldset>

      <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
        <legend className="mb-2 font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
          Who can buy it
        </legend>
        <Select
          variant="field"
          className={selectCls}
          value={value.eligibility.kind}
          disabled={readOnly}
          onChange={(kind) =>
            set({
              eligibility:
                kind === 'student_verification'
                  ? { kind, instructions: value.eligibility.instructions }
                  : // The API refuses instructions on an open package.
                    { kind: 'none', instructions: '' },
            })
          }
          options={[
            { value: 'none', label: 'Anyone' },
            { value: 'student_verification', label: 'Students (checked at the desk)' },
          ]}
          aria-label="Who can buy it"
        />
        {value.eligibility.kind === 'student_verification' && (
          <Field
            label="What the desk checks"
            required
            hint="Shown to whoever takes the first payment. They record what they saw; no document is uploaded."
            {...field('eligibility.instructions')}
          >
            <textarea
              rows={2}
              className={inputCls}
              value={value.eligibility.instructions}
              maxLength={500}
              disabled={readOnly}
              placeholder="A current student card with this term's date."
              onChange={(e) =>
                set({ eligibility: { kind: 'student_verification', instructions: e.target.value } })
              }
            />
          </Field>
        )}
      </fieldset>

      <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
        <legend className="mb-2 font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
          What it includes
        </legend>
        {value.benefits.length === 0 && (
          <p className="m-0 text-base text-ink-muted">
            No benefits listed. They are shown to customers; they do not create a balance.
          </p>
        )}
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {value.benefits.map((b, i) => {
            const title = inline(`benefits.${i}.title`);
            return (
              <li key={b.id} className="flex flex-col gap-2 rounded-xl border border-hairline p-3">
                <input
                  className={inputCls}
                  value={b.title}
                  maxLength={120}
                  disabled={readOnly}
                  placeholder="Desk access"
                  aria-label={`Benefit ${i + 1} title`}
                  onChange={(e) => setBenefit(i, { title: e.target.value })}
                  {...title.props}
                />
                <InlineError {...title.message} />
                {!readOnly && (
                  <div>
                    <Button
                      variant="quiet"
                      onClick={() => set({ benefits: value.benefits.filter((_, at) => at !== i) })}
                      aria-label={`Remove benefit ${i + 1}`}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {!readOnly && (
          <div>
            <Button variant="quiet" disabled={value.benefits.length >= 20} onClick={addBenefit}>
              Add benefit
            </Button>
          </div>
        )}
      </fieldset>
    </div>
  );
}
